import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ScrollText, Plus, Gavel } from "lucide-react";

export function AdminBoardMotions({ api }) {
  const [motions, setMotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", expires_in_hours: 72, min_quorum_pct: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try { setMotions((await api.get("/admin/motions")).data); }
    catch { toast.error("Could not load motions."); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required."); return;
    }
    setBusy(true);
    try {
      await api.post("/admin/motions", form);
      toast.success("Motion filed · Directors emailed.");
      setForm({ title: "", description: "", expires_in_hours: 72 });
      setOpenForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not file motion."); }
    finally { setBusy(false); }
  };

  const close = async (m) => {
    if (!window.confirm(`Close and tally the motion "${m.title}"?\nA signed Resolution PDF will be generated and emailed to every Director.`)) return;
    try {
      await api.post(`/admin/motions/${m.id}/close`);
      toast.success("Motion closed · Resolution PDF issued.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not close motion."); }
  };

  return (
    <div data-testid="admin-board-motions">
      <div className="rounded-sm border-2 border-[#C9A961]/40 bg-gradient-to-br from-[#141008] via-[#0f0d0a] to-[#141008] p-6 mb-8 flex items-start gap-5">
        <span className="grid place-items-center w-12 h-12 rounded-full bg-[#C9A961] text-black shrink-0">
          <Gavel size={22} />
        </span>
        <div className="flex-1">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">
            · Super Master Command · Motions & Resolutions ·
          </div>
          <h3 className="font-serif text-2xl text-[#F5F5F0] mt-1">Board Voting Module</h3>
          <p className="font-body text-sm text-[#C9C9C2] mt-2 leading-relaxed">
            File a motion, email every active Director, and close the vote to
            issue a tallied Board Resolution PDF under the Digital Seal.
          </p>
        </div>
        <button data-testid="motion-new-toggle" onClick={() => setOpenForm((v) => !v)}
          className="rounded-full bg-[#C9A961] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors inline-flex items-center gap-2">
          <Plus size={14} /> New motion
        </button>
      </div>

      {openForm && (
        <form onSubmit={create} data-testid="motion-form"
          className="mb-8 rounded-sm border border-[#C9A961]/30 bg-[#0f0d0a] p-6 grid gap-4">
          <label>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">Title</span>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="motion-title"
              className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-[#C9A961] outline-none py-2 font-body text-[#F5F5F0]" />
          </label>
          <label>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">Description / Motion text</span>
            <textarea required rows={4} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="motion-description"
              className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm p-3 font-body text-[#F5F5F0] outline-none" />
          </label>
          <label className="max-w-[220px]">
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">Voting window (hours)</span>
            <input type="number" min={1} max={720} value={form.expires_in_hours}
              onChange={(e) => setForm({ ...form, expires_in_hours: parseInt(e.target.value || "72", 10) })}
              data-testid="motion-window"
              className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-[#C9A961] outline-none py-2 font-body text-[#F5F5F0]" />
          </label>
          <label className="max-w-[280px]">
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">Min. shareholding quorum %</span>
            <input type="number" min={0} max={100} step="0.01" value={form.min_quorum_pct || 0}
              onChange={(e) => setForm({ ...form, min_quorum_pct: parseFloat(e.target.value || "0") })}
              data-testid="motion-quorum"
              placeholder="0 = no quorum requirement"
              className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-[#C9A961] outline-none py-2 font-body text-[#F5F5F0]" />
            <span className="font-body text-[10px] text-[#8A8A82]">
              If cap-table voted &lt; this %, motion closes as <em>no quorum</em>.
            </span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={busy} data-testid="motion-submit"
              className="rounded-sm bg-[#C9A961] text-black px-6 py-3 font-grotesk text-[10px] uppercase tracking-[0.28em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors disabled:opacity-60">
              {busy ? "Filing…" : "File motion"}
            </button>
            <button type="button" onClick={() => setOpenForm(false)}
              className="rounded-sm border border-white/20 px-6 py-3 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9C9C2] hover:border-white/40">
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="font-body text-[#8A8A82]">Loading…</p>
      ) : motions.length === 0 ? (
        <p className="font-body text-[#8A8A82]">No motions filed yet.</p>
      ) : (
        <div className="space-y-3">
          {motions.map((m) => (
            <div key={m.id} data-testid={`admin-motion-${m.id}`}
              className="rounded-sm border border-[#C9A961]/20 bg-[#0f0d0a] p-6 hover:border-[#C9A961]/50 transition-colors">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ScrollText size={14} className="text-[#C9A961]" />
                    <span className="font-serif text-lg text-[#F5F5F0]">{m.title}</span>
                    <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] border rounded-full px-2 py-0.5"
                      style={{
                        color: m.status === "open" ? "#C9A961" : "#8A8A82",
                        borderColor: m.status === "open" ? "#C9A961" : "#8A8A82",
                      }}>{m.status}</span>
                    {m.outcome && (
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] rounded-full px-2 py-0.5"
                        style={{ color: m.outcome === "passed" ? "#2FB999" : m.outcome === "rejected" ? "#C0392B" : m.outcome === "no_quorum" ? "#8A8A82" : "#C9A961",
                                  border: `1px solid ${m.outcome === "passed" ? "#2FB999" : m.outcome === "rejected" ? "#C0392B" : m.outcome === "no_quorum" ? "#8A8A82" : "#C9A961"}66` }}>
                        {m.outcome.replaceAll("_", " ")}
                      </span>
                    )}
                    {m.min_quorum_pct > 0 && (
                      <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961] border border-[#C9A961]/40 rounded-full px-2 py-0.5">
                        Quorum {m.min_quorum_pct}%
                      </span>
                    )}
                  </div>
                  <p className="font-body text-sm text-[#C9C9C2] mt-2">{m.description.slice(0, 200)}{m.description.length > 200 ? "…" : ""}</p>
                </div>
                {m.status === "open" && (
                  <button onClick={() => close(m)} data-testid={`motion-close-${m.id}`}
                    className="rounded-full bg-[#C9A961] text-black px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.24em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors">
                    Close & Tally
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#C9A961]/10">
                {["for", "against", "abstain"].map((k) => (
                  <div key={k} className="text-center">
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{k}</div>
                    <div className="font-serif text-2xl text-[#F5F5F0] tabular-nums">{m.vote_counts.counts[k]}</div>
                    <div className="font-body text-[10px] text-[#8A8A82]">{m.vote_counts.shares[k].toFixed(2)}% shares</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
